import { TrendingUp, TrendingDown, Minus } from "lucide-react";

// ─── TREEMAP ALGORITHM (Squarified) ─────────────────────
export function computeTreemap(nodes, w, h) {
  if (!nodes.length || !w || !h) return [];
  const total = nodes.reduce((s, n) => s + n.weight, 0);
  const sorted = [...nodes].sort((a, b) => (b.priority_score || b.weight) - (a.priority_score || a.weight));
  const area = w * h;
  const items = sorted.map(n => ({ ...n, _area: (n.weight / total) * area }));
  const out = [];
  squarify(items, { x: 0, y: 0, w, h }, out);
  return out;
}

function squarify(items, r, out) {
  if (!items.length) return;
  if (items.length === 1) {
    out.push({ ...items[0], _x: r.x, _y: r.y, _w: r.w, _h: r.h });
    return;
  }
  const shorter = Math.min(r.w, r.h);
  let row = [], rowArea = 0, best = Infinity, bestIdx = 0;
  for (let i = 0; i < items.length; i++) {
    row.push(items[i]);
    rowArea += items[i]._area;
    const worst = worstRatio(row, rowArea, shorter);
    if (worst <= best) { best = worst; bestIdx = i; } else break;
  }
  const lr = items.slice(0, bestIdx + 1), rest = items.slice(bestIdx + 1);
  const la = lr.reduce((s, d) => s + d._area, 0), strip = la / shorter, wide = r.w >= r.h;
  let off = 0;
  for (const item of lr) {
    const sz = item._area / strip;
    if (wide) out.push({ ...item, _x: r.x, _y: r.y + off, _w: strip, _h: sz });
    else out.push({ ...item, _x: r.x + off, _y: r.y, _w: sz, _h: strip });
    off += sz;
  }
  if (wide) squarify(rest, { x: r.x + strip, y: r.y, w: r.w - strip, h: r.h }, out);
  else squarify(rest, { x: r.x, y: r.y + strip, w: r.w, h: r.h - strip }, out);
}

function worstRatio(row, totalArea, shorter) {
  const strip = totalArea / shorter;
  let worst = 0;
  for (const item of row) {
    const s = item._area / strip;
    worst = Math.max(worst, Math.max(strip / s, s / strip));
  }
  return worst;
}

// ─── URGENCY COLOR SYSTEM (3-tier) ──────────────────────
export function getVisuals(state, urgency) {
  const u = urgency || 1;
  if (state === "red") {
    if (u >= 3) return {
      bg: "linear-gradient(145deg, rgba(220,38,38,0.45) 0%, rgba(185,28,28,0.18) 100%)",
      border: "rgba(239,68,68,0.55)", accent: "#f87171", glow: "0 0 50px rgba(239,68,68,0.25)",
      badge: "bg-red-500/20 text-red-300 border-red-500/30", label: "Urgent", pulse: true,
    };
    if (u >= 2) return {
      bg: "linear-gradient(145deg, rgba(239,68,68,0.35) 0%, rgba(220,38,38,0.12) 100%)",
      border: "rgba(239,68,68,0.4)", accent: "#f87171", glow: "0 0 40px rgba(239,68,68,0.18)",
      badge: "bg-red-500/15 text-red-400 border-red-500/25", label: "Attention Needed", pulse: false,
    };
    return {
      bg: "linear-gradient(145deg, rgba(248,113,113,0.25) 0%, rgba(239,68,68,0.08) 100%)",
      border: "rgba(248,113,113,0.3)", accent: "#fca5a5", glow: "0 0 30px rgba(248,113,113,0.12)",
      badge: "bg-red-400/10 text-red-300 border-red-400/20", label: "Early Warning", pulse: false,
    };
  }
  if (state === "yellow") {
    return {
      bg: u >= 2
        ? "linear-gradient(145deg, rgba(234,179,8,0.38) 0%, rgba(245,158,11,0.14) 100%)"
        : "linear-gradient(145deg, rgba(250,204,21,0.28) 0%, rgba(234,179,8,0.1) 100%)",
      border: u >= 2 ? "rgba(234,179,8,0.45)" : "rgba(250,204,21,0.3)",
      accent: "#fbbf24", glow: `0 0 ${u >= 2 ? 40 : 30}px rgba(234,179,8,0.${u >= 2 ? 18 : 12})`,
      badge: "bg-amber-500/15 text-amber-400 border-amber-500/20", label: u >= 2 ? "At Risk" : "Stable", pulse: false,
    };
  }
  return {
    bg: "linear-gradient(145deg, rgba(34,197,94,0.3) 0%, rgba(16,185,129,0.1) 100%)",
    border: "rgba(34,197,94,0.3)", accent: "#4ade80", glow: "0 0 25px rgba(34,197,94,0.1)",
    badge: "bg-emerald-500/15 text-emerald-400 border-emerald-500/20", label: "Improving", pulse: false,
  };
}

export const TREND_ICON = { improving: TrendingUp, worsening: TrendingDown, stable: Minus };
export const CONF_ICON = (c) => c > 0.8 ? "●" : c > 0.5 ? "◐" : "○";
export const TREND_ARROW = (t) => t === "worsening" ? "↑" : t === "improving" ? "↓" : "→";
